// External libraries
import { createStore } from 'vuex'
import createMultiTabState from 'vuex-multi-tab-state';
import { DecodeToken } from '@/store/JwtToken';

// Local modules
import { getBaseApiUrl, CompassApi } from '../api-client/CompassApiProxy';
import { CompanyListData, PropertySummary } from '../api-client/api-client'
import { DownloadedDataSet } from '@/store/DownloadedDataSet'

const getDefaultState = () => {
	return {
		apiUrl: getBaseApiUrl(),

		currentTicks: Date.now(),

		lastSuccessfulApiCall: 0,

		jwtToken: "",

		jwtExpiryTicks: 0,

		email: "",

		loginID: 0,

		companyList: new DownloadedDataSet<CompanyListData[]>([], ""),

		propertyList: new DownloadedDataSet<PropertySummary[]>([], ""),
	}
}

export default createStore({
	state: getDefaultState(),

	mutations: {
		CLEAR_STATE(state) {
			// clear existing state (especially if has access to different roles) -- will redirect to login automatically
			Object.assign(state, getDefaultState());
		},

		RESET_STATE_EXCEPT_TOKEN(state) {
			const currentToken = state.jwtToken;
			Object.assign(state, {
				...getDefaultState(),
				jwtToken: currentToken,
				jwtExpiryTicks: state.jwtExpiryTicks,
				email: state.email,
				loginID: state.loginID
			});
		},

		TIMER_UPDATE(state) {
			state.currentTicks = Date.now();
		},

		API_CONNECTED(state) {
			state.lastSuccessfulApiCall = Date.now();
		},

		UPDATE_COMPANYLIST_STATE(state, newCompanyListState) {
			state.companyList = newCompanyListState;
		},

		UPDATE_PROPERTYLIST_STATE(state, newPropertyListState) {
			state.propertyList = newPropertyListState;
		},

		LOGIN(state, jwt: string) {
			// ensure provided token can decode
			const token = DecodeToken(jwt);

			// Reset the state ready to apply the new jwtToken
			Object.assign(state, getDefaultState());

			// remember token details (and ticks separately because we will evaluate this every second)
			state.jwtToken = jwt;
			state.jwtExpiryTicks = token.exp * 1000;

			state.email = token.email;
			state.loginID = token.loginID;
		},

		LOGOUT(state) {
			// Clear state
			Object.assign(state, getDefaultState());
			// redirect to dashboard on logout
			location.replace(location.origin + '/compass/');
		},

	},

	actions: {
		clear_state(context) {
			context.commit('CLEAR_STATE')
		},

		updateTicks(context) {
			context.commit('TIMER_UPDATE')
		},

		apiConnected(context) {
			context.commit("API_CONNECTED");
		},

		login(context, payloadToken) {
			context.commit('LOGIN', payloadToken);
		},

		logout(context) {
			context.commit('LOGOUT');
		},

		tokenExpired(context) {
			context.commit('LOGOUT');
		},

		downloadCompanyList(context) {
			// Clear any error message
			context.commit('UPDATE_COMPANYLIST_STATE', DownloadedDataSet.mutateToLoading(context.state.companyList));

			// Download companies
			const apiClient = CompassApi.companyApi(this);
			apiClient.getCompanyListIfDifferent(100000, context.state.companyList.hash)
				.then(result => {
					// 200 received new data
					context.commit('UPDATE_COMPANYLIST_STATE', DownloadedDataSet.mutateToDownloaded(context.state.companyList, result.data, result.hash as string));
				})
				.catch((error: Error) => {
					if ((error as any).status == 304) {
						// 304 not modified
						context.commit('UPDATE_COMPANYLIST_STATE', DownloadedDataSet.mutateToNotModified(context.state.companyList));
						return;
					}
					const errorMessage = CompassApi.translateError('Retrieving company list failed', error, true);
					context.commit('UPDATE_COMPANYLIST_STATE', DownloadedDataSet.mutateToError(context.state.companyList, errorMessage));
				});
		},

		downloadPropertyList(context) {
			// Clear any error message
			context.commit('UPDATE_PROPERTYLIST_STATE', DownloadedDataSet.mutateToLoading(context.state.propertyList));

			// Download properties
			const apiClient = CompassApi.propertyApi(this);
			apiClient.getPropertyListIfDifferent(100000, context.state.propertyList.hash)
				.then(result => {
					// 200 received new data
					context.commit('UPDATE_PROPERTYLIST_STATE', DownloadedDataSet.mutateToDownloaded(context.state.propertyList, result.data, result.hash as string));
				})
				.catch((error: any) => {
					if ((error as any).status == 304) {
						// 304 not modified
						context.commit('UPDATE_PROPERTYLIST_STATE', DownloadedDataSet.mutateToNotModified(context.state.propertyList));
						return;
					}
					const errorMessage = CompassApi.translateError('Retrieving property list failed', error, true);
					context.commit('UPDATE_PROPERTYLIST_STATE', DownloadedDataSet.mutateToError(context.state.propertyList, errorMessage));
				});
		},

		refreshApp(context) {
			context.commit('RESET_STATE_EXCEPT_TOKEN');
			location.reload()
		}

	},

	modules: {
	},

	getters: {

		isAuthenticated: (state, getters) => {
			// This getter needs to reference currentTicks (directly or via sessionTimeRemaining etc) so we re-evaluate this every second
			return state.jwtToken && state.jwtToken.length > 2 && getters.sessionTimeRemaining > 30;
		},

		jwtRoles: (state, getters) => {
			return JSON.stringify(DecodeToken(state.jwtToken).Role);
		},

		isInternalUser: (state, getters) => {
			const roles = getters.jwtRoles;
			return (roles != null) && (roles.indexOf('CompassStaff') >= 0 || roles.indexOf('CompassAdmin') >= 0);
		},

		isCustomer: (state, getters) => {
			const roles = getters.jwtRoles;
			return (roles != null) && (roles.indexOf('ExternalCustomer') >= 0);
		},

		isSupplier: (state, getters) => {
			const roles = getters.jwtRoles;
			return (roles != null) && (roles.indexOf('ExternalSupplier') >= 0);
		},

		secondsSinceLastConnection: state => {
			return Math.floor((state.currentTicks - state.lastSuccessfulApiCall) / 1000);
		},

		sessionTimeRemaining: (state, getters) => {
			return Math.floor((state.jwtExpiryTicks - state.currentTicks) / 1000);
		},

		localStorageSize: (state, getters): string => {
			let _lsTotal = 0, _xLen: number, _x: any;

			// reference sessionTimeRemaining so that this is updated every second
			if (getters.sessionTimeRemaining < 1)
				return ' - ';

			// Iterate local storage
			for (_x in localStorage) {
				if (!Object.prototype.hasOwnProperty.call(localStorage, _x)) {
					continue;
				}
				_xLen = ((localStorage[_x].length + _x.length) * 2);
				_lsTotal += _xLen;
			}
			return "LocalStorage = " + (_lsTotal / 1024).toFixed(0) + " KB";
		},
	},

	plugins: [
		createMultiTabState({
			statesPaths: ['jwtToken', 'jwtExpiryTicks', 'lastSuccessfulApiCall', 'companyList', 'propertyList', 'email', 'loginID'],
		}),
	],
})